export const achievementsHero = {
  eyebrow: 'Achievements & Milestones',
  titleLine1: 'Celebrating Our',
  titleAccent: 'Growth & Excellence',
  description:
    'Our achievements reflect our dedication to quality, reliability, customer satisfaction and operational excellence in metal trading.',
  primaryCta: { label: 'View Milestones', to: '#achievements-milestones' },
  secondaryCta: { label: 'Contact Us', to: '/contact#inquiry-form' },
}

export const achievementsMilestonesSection = {
  label: 'Our Achievements',
  titleLine1: 'Milestones That Define',
  titleAccent: 'Our Success',
  description:
    'Through strong partnerships and disciplined execution, we continue to expand our market reach while delivering reliable metal supply, client satisfaction and sustainable business growth.',
  milestones: [
    {
      id: 'supply-orders',
      number: '01',
      icon: 'trophy',
      title: 'Successful Execution of High-Volume Metal Supply Orders',
      description:
        'Delivering large-scale industrial supply with structured planning, dependable sourcing and efficient fulfillment.',
      highlights: [
        'Large-scale supply execution',
        'Reliable sourcing network',
        'Efficient logistics coordination',
        'Consistent material availability',
        'Timely order fulfillment',
      ],
    },
    {
      id: 'client-satisfaction',
      number: '02',
      icon: 'partnership',
      title: 'High Level of Client Satisfaction & Repeat Business',
      description:
        'Building long-term trust through transparent communication, dependable service and strong repeat partnerships.',
      highlights: [
        'Long-term customer relationships',
        'Strong client retention',
        'Repeat business partnerships',
        'Reliable service delivery',
        'Customer-focused approach',
      ],
    },
    {
      id: 'business-expansion',
      number: '03',
      icon: 'growth',
      title: 'Consistent Growth & Business Expansion',
      description:
        'Expanding capabilities through strategic growth, stronger supplier networks and increased operational strength.',
      highlights: [
        'Continuous organizational growth',
        'Expanded business opportunities',
        'Strong supplier partnerships',
        'Increased operational capabilities',
        'Market expansion initiatives',
      ],
    },
  ],
}

export const achievementsSuccessFactorsSection = {
  label: 'What Drives Our Success',
  titleLine1: 'Key Success',
  titleAccent: 'Factors',
  factors: [
    {
      id: 'supply-chain',
      icon: 'supply-chain',
      title: 'Reliable Supply Chain',
      description: 'Maintaining dependable sourcing channels and logistics support.',
    },
    {
      id: 'quality',
      icon: 'quality',
      title: 'Quality Commitment',
      description: 'Ensuring materials meet strict quality standards.',
    },
    {
      id: 'market-knowledge',
      icon: 'market-knowledge',
      title: 'Strong Market Knowledge',
      description: 'Understanding industry trends and global markets.',
    },
    {
      id: 'partnerships',
      icon: 'partnerships',
      title: 'Strategic Partnerships',
      description: 'Building long-term supplier and client relationships.',
    },
    {
      id: 'satisfaction',
      icon: 'satisfaction',
      title: 'Customer Satisfaction',
      description: 'Delivering reliable service and consistent support.',
    },
    {
      id: 'global-trade',
      icon: 'global-trade',
      title: 'Global Trade Capability',
      description: 'Executing cross-border metal trading efficiently.',
    },
  ],
}

export const achievementsOperationalSection = {
  label: 'Operational Strength',
  titleLine1: 'How We Deliver',
  titleAccent: 'Consistent Results',
  description:
    'Our progress is built on disciplined sourcing, transparent communication and reliable execution across every stage of metal trading — from enquiry and procurement to documentation, logistics and final delivery.',
  pillars: [
    {
      id: 'sourcing',
      number: '01',
      title: 'Structured Sourcing & Supplier Development',
      description:
        'We maintain dependable supplier relationships and evaluate material grades, specifications and commercial terms before confirming supply.',
      points: [
        'Verified sourcing channels',
        'Grade and specification review',
        'Competitive commercial positioning',
      ],
    },
    {
      id: 'execution',
      number: '02',
      title: 'Order Planning & Fulfillment',
      description:
        'High-volume and recurring industrial orders are managed with clear timelines, packing preferences and delivery coordination.',
      points: [
        'Large-scale supply execution',
        'Packing and dispatch planning',
        'Timely order fulfillment',
      ],
    },
    {
      id: 'logistics',
      number: '03',
      title: 'Logistics & Documentation Support',
      description:
        'Our team coordinates dispatch, transport requirements and trade documentation to keep movement efficient and professionally managed.',
      points: [
        'Dispatch and transport coordination',
        'Documentation and compliance support',
        'Supply chain visibility',
      ],
    },
    {
      id: 'partnerships',
      number: '04',
      title: 'Long-Term Client Partnerships',
      description:
        'We prioritize service consistency, responsive communication and repeat business relationships across domestic and international markets.',
      points: [
        'Transparent client communication',
        'Repeat business partnerships',
        'Responsive enquiry handling',
      ],
    },
  ],
  focusAreas: [
    {
      id: 'markets',
      title: 'Markets We Serve',
      text: 'India, GCC, Africa and wider international trading markets through structured export and domestic supply coordination.',
    },
    {
      id: 'products',
      title: 'Materials We Trade',
      text: 'Aluminium, copper, brass, zinc, lead, stainless steel, industrial scrap, ingots and related raw materials for diverse industries.',
    },
    {
      id: 'commitment',
      title: 'What Clients Value Most',
      text: 'Reliable availability, quality-focused sourcing, competitive pricing and professional execution from enquiry to delivery.',
    },
  ],
}

export const achievementsFutureGoalsSection = {
  label: 'Looking Ahead',
  titleLine1: 'Our Future',
  titleAccent: 'Goals',
  goals: [
    {
      id: 'global-presence',
      icon: 'global-presence',
      title: 'Expand Global Presence',
      description: 'Increase our footprint in international markets.',
    },
    {
      id: 'partnerships',
      icon: 'partnerships',
      title: 'Strengthen Strategic Partnerships',
      description: 'Build long-term relationships with suppliers and clients.',
    },
    {
      id: 'efficiency',
      icon: 'efficiency',
      title: 'Enhance Operational Efficiency',
      description: 'Improve processes through technology and innovation.',
    },
    {
      id: 'quality',
      icon: 'quality',
      title: 'Improve Quality Standards',
      description: 'Maintain superior quality in every supply.',
    },
    {
      id: 'sustainability',
      icon: 'sustainability',
      title: 'Sustainable Growth',
      description: 'Grow responsibly with a commitment to sustainability.',
    },
    {
      id: 'customer-value',
      icon: 'customer-value',
      title: 'Increase Customer Value',
      description: 'Deliver greater value through better service and support.',
    },
  ],
}

export const achievementsCommitmentSection = {
  label: 'Our Commitment',
  titleLine1: 'Driven by Values. Focused on',
  titleAccent: 'Excellence.',
  description:
    'Every achievement reflects our dedication to professionalism, transparency, reliability and customer satisfaction. We continue to strengthen our capabilities and pursue new opportunities while maintaining the highest standards of business conduct and operational performance.',
  stats: [
    {
      id: 'clients',
      icon: 'clients',
      value: '100',
      suffix: '+',
      title: 'Happy Clients',
      subtitle: 'Across India & International Markets',
    },
    {
      id: 'orders',
      icon: 'orders',
      value: '500',
      suffix: '+',
      title: 'Successful Orders',
      subtitle: 'Executed with Excellence',
    },
    {
      id: 'countries',
      icon: 'countries',
      value: '10',
      suffix: '+',
      title: 'Countries Served',
      subtitle: 'Global Presence and Sourcing',
    },
    {
      id: 'growth',
      icon: 'growth',
      value: '25',
      suffix: '%+',
      title: 'Year-on-Year Growth',
      subtitle: 'Consistent Growth Every Year',
    },
  ],
}
